/**
 * Tanstack Query wrapper hooks for loan data.
 */

import { useQuery } from '@tanstack/react-query';
import { loanService } from '@/services/loan';
import { applicationKeys } from '@/services/wizard';
import type {
  LoanCompositeResult,
  LoanHeaderResult,
  TimelineResult,
  SnapshotResult,
  PricingAndLockResult,
  MonthlyHousingCosts,
  FundsAtClosingResult,
  LoanContactsResult,
  NeedsAttentionResult,
  HomeEquityResult,
  PaymentHistoryResult,
} from '@/types/loan';

// ─── Composite loan-read (loan-read consolidation, step 3/3) ───────────────────
//
// The granular per-slice GET routes were removed from the API — the loan-detail
// page reads every section in ONE trip via GET /loans/{id}?include=<sections>.
// Each per-slice hook below queries the SAME composite key + queryFn and `select`s
// its own slice; TanStack Query dedupes the concurrent observers into a single
// network request. Hook return contracts are unchanged, so consumers
// (Dashboard.tsx, MyLoan.tsx, …) are untouched.

const COMPOSITE_SECTIONS = [
  // NP-234: `header` is a SECONDARY source for the loan TYPE. It rides along on
  // the request the page already makes, but returns loanType hard-null for
  // Vesta-created loans (`TODO(vesta-reads)`), so the primary is now
  // `GET /application/{id}` → `loanProduct` (afn-path-api#76).
  'header',
  'timeline',
  'snapshot',
  'pricing-and-lock',
  'monthly-housing-cost',
  'funds-at-closing',
  'contacts',
  'needs-attention',
  'home-equity',
  'payments',
] as const;

const COMPOSITE_STALE_TIME = 30_000;

const compositeQueryKey = (loanId: string | undefined) => ['loan', loanId, 'composite'] as const;

const fetchComposite = (loanId: string) => loanService.getLoanComposite(loanId, COMPOSITE_SECTIONS);

/** Extract a section (camelCase key) from the composite payload. Missing/failed → undefined. */
function selectSection<T>(key: string) {
  return (data: LoanCompositeResult): T | undefined =>
    (data?.sections?.[key] ?? undefined) as T | undefined;
}

/** Full loan data query */
export function useLoanQuery(loanId: string | undefined) {
  return useQuery({
    queryKey: ['loan', loanId],
    queryFn: async () => {
      // Ensure loanId is available before making any API calls
      if (!loanId) {
        throw new Error('Loan ID is required');
      }
      return loanService.getLoanApplication(loanId);
    },
    enabled: !!loanId,
    staleTime: 30_000,
    // Don't retry auth errors or undefined loanId scenarios
    retry: (failureCount, error) => {
      // Don't retry if loanId is undefined
      if (!loanId) return false;
      
      // Don't retry 401/403/404 auth errors
      if (error && 'status' in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403 || status === 404) {
          return false;
        }
      }
      
      // Only retry once for other errors
      return failureCount < 1;
    },
    // P0 WHITE-PAGE FIX: never throw to ErrorBoundary from useLoanQuery.
    // LoanProvider (which calls this hook) sits ABOVE the ErrorBoundary in the
    // component tree (App.tsx: ErrorBoundary is inside AppRoutes which is inside
    // LoanProvider). A throwOnError:true here escapes the ErrorBoundary and
    // unmounts the entire React tree — blank white page.
    // Callers handle errors via the returned { error } object instead.
    throwOnError: false,
  });
}

/**
 * Loan header — high-level loan-of-record summary (`LoanHeaderResponse`).
 * loanNumber/status/currentStage/loanAmount/interestRate are overlaid live from
 * Vesta for submitted loans; loanType/loanTerm/property are still null-by-design
 * server-side (`TODO(vesta-reads)`), so consumers MUST treat them as optional.
 */
export function useLoanHeader(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<LoanHeaderResult>('header'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Timeline stages */
export function useTimeline(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<TimelineResult>('timeline'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Loan snapshot */
export function useSnapshot(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<SnapshotResult>('snapshot'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Pricing and lock info */
export function usePricingAndLock(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<PricingAndLockResult>('pricingAndLock'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Monthly housing costs */
export function useMonthlyHousingCosts(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<MonthlyHousingCosts>('monthlyHousingCost'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Funds at closing */
export function useFundsAtClosing(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<FundsAtClosingResult>('fundsAtClosing'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Open conditions */
export function useOpenConditions(loanId: string | undefined) {
  return useQuery({
    queryKey: ['loan', loanId, 'open-conditions'],
    queryFn: () => loanService.getOpenConditions(loanId!),
    enabled: !!loanId,
    staleTime: 30_000,
  });
}

/** Loan contacts */
export function useLoanContacts(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<LoanContactsResult>('contacts'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/**
 * NP-505 QA: contacts ONLY, on its own key.
 *
 * DBA theming needs one field — the loan officer's address — but reading it off the
 * shared composite meant the branch colours could not appear until all ten sections
 * had returned. QA measured 6–8s of default gold on a loan that themes blue, with
 * the repaint landing on the same frame as the loan figures. This asks for the one
 * section instead, so the theme tracks the loan rather than the slowest section on
 * the page. The extra request is the point: it is small, and it is not on the
 * critical path of anything the composite serves.
 */
export function useLoanOfficerContacts(loanId: string | undefined) {
  return useQuery({
    queryKey: ['loan', loanId, 'contacts-only'] as const,
    queryFn: () => loanService.getLoanComposite(loanId!, ['contacts']),
    select: selectSection<LoanContactsResult>('contacts'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Every borrower-facing condition on the loan (PATH's own shape). */
export function useLoanConditions(loanId: string | undefined) {
  return useQuery({
    queryKey: ['loan', loanId, 'conditions'],
    queryFn: () => loanService.getLoanConditions(loanId!),
    enabled: !!loanId,
    staleTime: 30_000,
  });
}

/** Detailed open conditions */
export function useOpenConditionDetails(loanId: string | undefined) {
  return useQuery({
    queryKey: ['loan', loanId, 'open-conditions', 'details'],
    queryFn: () => loanService.getOpenConditionDetails(loanId!),
    enabled: !!loanId,
    staleTime: 30_000,
  });
}

/** Needs attention items */
export function useNeedsAttention(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<NeedsAttentionResult>('needsAttention'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
    // Don't throw to ErrorBoundary — let components handle errors gracefully
    throwOnError: false,
    // Don't retry auth/not-found errors
    retry: (failureCount, error) => {
      if (error && 'status' in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
      }
      return failureCount < 1;
    },
  });
}

/** Home equity data — estimated value, balance, equity, history */
export function useHomeEquity(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<HomeEquityResult>('homeEquity'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}

/** Incomplete applications for current borrower */
export function useIncompleteApplications() {
  return useQuery({
    queryKey: applicationKeys.incomplete,
    queryFn: async () => {
      try {
        return await loanService.getIncompleteApplications();
      } catch (error) {
        // For fresh users, incomplete applications API might fail
        // Return empty array to allow them to start a new application
        // Expected before sign-in: no auth provider is wired yet, or the API
        // answers 401. Returning [] is the designed fresh-user path, so don't
        // dress it up as a warning (console-noise cleanup 2026-07-29).
        const listStatus = (error as { status?: number } | null)?.status;
        const anonymous =
          listStatus === 401 ||
          listStatus === 403 ||
          /no authentication provider/i.test(String((error as Error | null)?.message ?? ''));
        if (anonymous) {
          if (import.meta.env.DEV) {
            console.debug('[useIncompleteApplications] not signed in — returning no loans');
          }
        } else {
          console.warn('[useIncompleteApplications] API failed, returning empty array:', error);
        }
        return [];
      }
    },
    staleTime: 60_000,
    // Don't retry auth errors - they won't self-resolve
    retry: (failureCount, error) => {
      // Don't retry 401/403 auth errors, especially in mixed mode
      if (error && 'status' in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403) return false;
      }
      // Only retry once for other errors (network, temporary issues)
      return failureCount < 1;
    },
    // Don't propagate errors to avoid breaking fresh user flows
    throwOnError: false,
  });
}

/** Payment history */
export function usePaymentHistory(loanId: string | undefined) {
  return useQuery({
    queryKey: compositeQueryKey(loanId),
    queryFn: () => fetchComposite(loanId!),
    select: selectSection<PaymentHistoryResult>('payments'),
    enabled: !!loanId,
    staleTime: COMPOSITE_STALE_TIME,
  });
}
